import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/providers/AuthProvider';
import { roleSatisfies } from '@/lib/roleRank';
import { consoleApi, type IngestedDoc } from '@/lib/consoleApi';
import { AdminPage, RegistryTable, Pill } from '@/admin';
import { DOMAIN_HEX, DOMAIN_LABELS } from '@/lib/compounds';

const PAGE = 50;

export function IngestedPage() {
  const qc = useQueryClient();
  const { role } = useAuth();
  const isAdmin = roleSatisfies(role ?? '', 'admin');
  const [query, setQuery] = useState('');
  const [domain, setDomain] = useState('');
  const [source, setSource] = useState('');
  const [offset, setOffset] = useState(0);

  const stats = useQuery({ queryKey: ['moderation-stats'], queryFn: () => consoleApi.moderation.stats() });
  const filters = { query: query || undefined, domain: domain || undefined, source: source || undefined, limit: PAGE, offset };
  const q = useQuery({ queryKey: ['ingested', filters], queryFn: () => consoleApi.moderation.ingested(filters) });

  const del = useMutation({
    mutationFn: (id: string) => consoleApi.moderation.deleteIngested(id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['ingested'] }); void qc.invalidateQueries({ queryKey: ['moderation-stats'] }); },
    onError: (e: unknown) => alert(e instanceof Error ? e.message : 'Error deleting document'),
  });

  const docs = q.data?.documents ?? [];
  const total = q.data?.total ?? 0;
  const domains = Object.keys(stats.data?.stats ?? {}).sort();
  const resetPage = () => setOffset(0);

  return (
    <AdminPage
      title="Ingested — Research Database"
      description="The curated research corpus already ingested into the Research Database. Browse by domain, source, or full-text search."
    >
      {stats.data ? (
        <div className="mb-13 text-body-sm text-text-secondary">
          <strong className="text-text-primary">{stats.data.total_documents ?? '—'}</strong> documents ·
          {' '}{stats.data.total_chunks ?? '—'} chunks
          {stats.data.error ? <span className="text-flagged"> · {stats.data.error}</span> : ''}
        </div>
      ) : null}

      <div className="mb-13 flex flex-wrap items-center gap-8">
        <input
          value={query} onChange={(e) => { setQuery(e.target.value); resetPage(); }}
          placeholder="Full-text search…"
          className="w-[240px] rounded-sm border border-border-strong px-2.5 py-2 text-body-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
        />
        <select value={domain} onChange={(e) => { setDomain(e.target.value); resetPage(); }}
          className="rounded-sm border border-border-strong bg-surface px-2 py-2 text-body-sm">
          <option value="">All domains</option>
          {domains.map((d) => <option key={d} value={d}>{DOMAIN_LABELS[d] ?? d}</option>)}
        </select>
        <input
          value={source} onChange={(e) => { setSource(e.target.value); resetPage(); }}
          placeholder="Source (e.g. cochrane, pubmed)…"
          className="w-[200px] rounded-sm border border-border-strong px-2.5 py-2 text-body-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
        />
      </div>

      <RegistryTable<IngestedDoc>
        columns={[
          { key: 'title', header: 'Document', render: (d) => (
            <div>
              <div className="font-medium">{d.title || <span className="text-text-muted">(untitled)</span>}</div>
              <div className="flex flex-wrap gap-2 text-[11px] text-text-muted">
                {d.pubmed_id ? <a href={`https://pubmed.ncbi.nlm.nih.gov/${d.pubmed_id}/`} target="_blank" rel="noreferrer" className="text-accent hover:underline">PMID {d.pubmed_id}</a> : null}
                {d.doi ? <a href={`https://doi.org/${d.doi}`} target="_blank" rel="noreferrer" className="text-accent hover:underline">DOI</a> : null}
                {d.document_date ? <span>{d.document_date}</span> : null}
              </div>
            </div>
          ) },
          { key: 'source', header: 'Source', className: 'text-[12px] text-text-secondary', render: (d) => d.source || '—' },
          { key: 'domain', header: 'Domain', render: (d) => d.domain_tag ? <Pill label={DOMAIN_LABELS[d.domain_tag] ?? d.domain_tag} color={DOMAIN_HEX[d.domain_tag]} /> : <span className="text-text-muted text-[12px]">—</span> },
          { key: 'type', header: 'Type', className: 'text-[12px] text-text-secondary', render: (d) => d.source_type || '—' },
          { key: 'tier', header: 'Tier', className: 'font-mono text-[12px]', render: (d) => d.evidence_tier ?? '—' },
        ]}
        rows={docs}
        getRowKey={(d) => d.document_id}
        count={total}
        state={{ loading: q.isLoading, error: q.isError }}
        emptyText={query || domain || source ? 'No ingested documents match these filters.' : 'No documents ingested yet.'}
        rowActions={isAdmin ? (d) => (
          <Button variant="destructive" size="sm" onClick={() => { if (confirm(`Delete "${d.title || d.document_id}" from the Research Database? This removes all its chunks.`)) del.mutate(d.document_id); }}>Delete</Button>
        ) : undefined}
      />

      {total > PAGE ? (
        <div className="mt-13 flex items-center justify-between text-body-sm">
          <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>← Prev</Button>
          <span className="text-text-secondary">{offset + 1}–{Math.min(offset + PAGE, total)} of {total}</span>
          <Button variant="outline" size="sm" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>Next →</Button>
        </div>
      ) : null}
    </AdminPage>
  );
}
